import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { getOutfitStats } from '@/services/outfitsService';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ShoppingBag, 
  Package, 
  ArrowUpRight, 
  BarChart,
  Plus,
  Image as ImageIcon,
  Heart,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { Database } from '@/integrations/supabase/types';

type ClothingRow = Database['public']['Tables']['products']['Row'];

type BaseItem = {
  _id: number;
  name: string;
  image: string;
  price: number;
  brand: string;
  batch: string;
  "category[0]": string;
  "category[1]": string;
  "category[2]": string;
  created_at: string;
  created_by: string;
  link: string;
  updated_at: string;
};

interface RecentItem extends BaseItem {
  type: 'clothing';
  itemUrl: string;
}

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalItems: 0,
    clothingCategories: [] as {name: string, value: number}[]
  });
  const [outfitStats, setOutfitStats] = useState({
    totalOutfits: 0,
    totalLikes: 0,
    totalViews: 0,
    publicOutfits: 0,
  });
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { count: totalClothing } = await supabase
          .from('products')
          .select('*', { count: 'exact', head: true });

        const { data: recentClothing, error: recentClothingError } = await supabase
          .from('products')
          .select('*')
          .order('_id', { ascending: false })
          .limit(6);
          
        if (recentClothingError) {
          console.error('Error fetching recent clothing items:', recentClothingError);
          toast.error('Failed to load recent clothing items');
          return;
        }

        // Fetch outfit stats
        try {
          const oStats = await getOutfitStats();
          setOutfitStats(oStats);
        } catch (e) {
          console.error('Error fetching outfit stats:', e);
        }

        setStats({
          totalItems: totalClothing || 0,
          clothingCategories: []
        });

        // Format recent items
        const formattedRecentItems = recentClothing?.map(item => ({
          ...item,
          type: 'clothing' as const,
          itemUrl: `/admin/clothinglist/${item._id}`
        })) || [];
        
        setRecentItems(formattedRecentItems);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <div>
          <h1 className="text-3xl font-bold mb-1">Dashboard</h1>
          <p className="text-muted-foreground">Welcome to your Repsheets admin dashboard</p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
          <Link to="/admin/clothinglist/new">
            <Button className="flex items-center gap-2">
              <Plus size={16} />
              Add Product
            </Button>
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center h-60 bg-card rounded-lg border">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
            <p className="text-sm text-muted-foreground">Loading dashboard data...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="overflow-hidden border-l-4 border-l-primary bg-gradient-to-br from-card to-background">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Total Products</p>
                    <p className="text-3xl font-bold">{stats.totalItems}</p>
                  </div>
                  <div className="rounded-full bg-primary/10 p-3">
                    <ShoppingBag className="h-8 w-8 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden border-l-4 border-l-emerald-500 bg-gradient-to-br from-card to-background">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Total Outfits</p>
                    <p className="text-3xl font-bold">{outfitStats.totalOutfits}</p>
                  </div>
                  <div className="rounded-full bg-emerald-500/10 p-3">
                    <ImageIcon className="h-8 w-8 text-emerald-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden border-l-4 border-l-rose-500 bg-gradient-to-br from-card to-background">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Total Likes</p>
                    <p className="text-3xl font-bold">{outfitStats.totalLikes}</p>
                  </div>
                  <div className="rounded-full bg-rose-500/10 p-3">
                    <Heart className="h-8 w-8 text-rose-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden border-l-4 border-l-amber-500 bg-gradient-to-br from-card to-background">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Total Views</p>
                    <p className="text-3xl font-bold">{outfitStats.totalViews}</p>
                  </div>
                  <div className="rounded-full bg-amber-500/10 p-3">
                    <Eye className="h-8 w-8 text-amber-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Card className="overflow-hidden">
              <CardHeader className="border-b bg-card/50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5 text-primary" />
                      Recent Products
                    </CardTitle>
                    <CardDescription>Latest products added to the system</CardDescription>
                  </div>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/admin/clothinglist">View All</Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y max-h-[400px] overflow-y-auto scrollbar-none">
                  {recentItems.length === 0 ? (
                    <div className="p-6 text-center text-muted-foreground">
                      No products found
                    </div>
                  ) : (
                    recentItems.map((item) => (
                      <Link 
                        key={`${item.type}-${item._id}`} 
                        to={item.itemUrl}
                        className="flex items-center hover:bg-muted/50 p-4 transition-colors"
                      >
                        <div className="h-14 w-14 rounded-md overflow-hidden flex-shrink-0 bg-muted mr-4 border">
                          {item.image ? (
                            <img 
                              src={item.image} 
                              alt={item.name}
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = '/placeholder.svg';
                              }}
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center bg-muted">
                              <Package className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.name}</p>
                          <div className="flex items-center mt-1 gap-2">
                            <Badge variant="secondary" className="text-xs font-normal">
                              {item.brand}
                            </Badge>
                            <Badge variant="outline" className="text-xs font-normal">
                              {item["category[0]"] || "Uncategorized"}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            <span className="font-medium text-primary">${item.price?.toFixed(2)}</span> · Added {new Date(item.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="ml-2">
                          <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              <CardHeader className="border-b bg-card/50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <ImageIcon className="h-5 w-5 text-primary" />
                      Outfit Overview
                    </CardTitle>
                    <CardDescription>Key outfit metrics at a glance</CardDescription>
                  </div>
                  <Button asChild size="sm" variant="outline">
                    <Link to="/admin/outfits">View All</Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/30 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-500">{outfitStats.publicOutfits}</p>
                    <p className="text-xs text-muted-foreground mt-1">Public Outfits</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold">{outfitStats.totalOutfits - outfitStats.publicOutfits}</p>
                    <p className="text-xs text-muted-foreground mt-1">Private Outfits</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-rose-500">{outfitStats.totalLikes}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total Likes</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-amber-500">{outfitStats.totalViews}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total Views</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button asChild size="sm" variant="outline" className="flex-1">
                    <Link to="/admin/outfits">
                      <ImageIcon className="h-4 w-4 mr-1" /> Manage Outfits
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="outline" className="flex-1">
                    <Link to="/admin/users">
                      <Heart className="h-4 w-4 mr-1" /> View Users
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminDashboard;
